import React, { useState, useCallback, useRef } from 'react';
import {
  Chip,
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/Folder';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const ActionsCopyFile = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [targetPath, setTargetPath] = useState('');
  const [sourcePath, setSourcePath] = useState('');
  const [sourceBrowsingDisabled, setSourceBrowsingDisabled] = useState(false);
  const [browsingDisabled, setBrowsingDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const [replaceIfExists, setReplaceIfExists] = useState(false);
  const [replaceWarningOpen, setReplaceWarningOpen] = useState(false);
  
  const targetRef = useRef(null);
  const sourceRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/\/+$/, "");
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        folderPathClean = folderPathClean + separator;
        setTargetPath(folderPathClean + "{filename}.{file_extension}");
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowsingDisabled(false);
    }
  });

  const customDataTags = [
    { key: 'tag_fileContentToWrite', label: 'Data Target File Content (if exist)', value: 'fileContentToWrite', description: 'Content of the target file if it already exists' }
  ];

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'copyfile',
    onSaveStatusChange,
    customComparison: (initial) => {
      return (
        targetPath === (initial.target || '') &&
        sourcePath === (initial.sourceFilePath || '') &&
        replaceIfExists === Boolean(initial.replaceIfExists)
      );
    },
    customSaveData: () => ({
      target: targetPath,
      sourceFilePath: sourcePath,
      replaceIfExists
    }),
    loadDataFunction: (data) => {
      if (data.customData?.target !== undefined) {
        setTargetPath(data.customData.target);
      }
      if (data.customData?.sourceFilePath !== undefined) {
        setSourcePath(data.customData.sourceFilePath);
      }
      if (data.customData?.replaceIfExists !== undefined) {
        setReplaceIfExists(Boolean(data.customData.replaceIfExists));
      }
    }
  });
  const baseHandleSave = actionSettings.handleSave;

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    if (menuType === 'target') {
      targetRef.current?.insertTag(value);
    }
    if (menuType === 'source') {
      sourceRef.current?.insertTag(value);
    }
    setAnchorEl(null);
  };

  const handleSave = useCallback(() => {
    if (replaceIfExists) {
      setReplaceWarningOpen(true);
      return;
    }
    baseHandleSave();
  }, [replaceIfExists, baseHandleSave]);

  const handleConfirmReplaceSave = useCallback(() => {
    setReplaceWarningOpen(false);
    baseHandleSave();
  }, [baseHandleSave]);

  return (
    <ActionSettingsLayout
      title={t('workflow.copy_files_title', 'Copy Files')}
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#5757b0"
    >
      {/* Source file (optional) */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                SOURCE
              </Box>
              source file (optional)
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 8px 20px rgba(87, 87, 176, 0.1)',
            },
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                flex: 1,
                padding: '12px 16px',
                minHeight: '28px',
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                overflow: 'hidden',
                borderBottom: { xs: '1px solid #eaeaf0', sm: 'none' },
              }}>
                <TagEditor
                    caseid={caseid}
                  ref={sourceRef}
                  value={sourcePath}
                  onChange={setSourcePath}
                  placeholder="Source file path (e.g. C:\\Folder\\{filename}{file_extension})"
                  minHeight="28px"
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'source')}
                  tags={tags}
                />
              </Box>
              <Button
                variant="contained"
                onClick={() => {
                  setSourceBrowsingDisabled(true);
                  handleBrowseSourceFile();
                }}
                disabled={sourceBrowsingDisabled}
                sx={{
                  height: { xs: '40px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: { xs: '0', sm: '0 12px 0 0' },
                  backgroundColor: '#5757b0',
                  color: 'white',
                  m: 0,
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#4a4a90',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#c0c0d0',
                    color: 'white',
                  },
                  borderLeft: { sm: '1px solid #5757b060' },
                  transition: 'all 0.2s ease',
                }}
              >
                <BrowseIcon />
              </Button>
            </Box>

            <Box sx={{
              p: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              backgroundColor: '#fafbff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{
                fontWeight: 600,
                color: '#5757b0',
                fontSize: '0.85rem',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
              }}>
                Quick Tags:
              </Typography>
              {[
                { label: 'Filename', value: 'filename' },
                { label: 'Extension', value: 'file_extension' },
                { label: 'Folder Path', value: 'file_folder_path' },
                { label: 'Year', value: 'year' },
                { label: 'Month', value: 'month' },
                { label: 'Day', value: 'day' },
              ].map(tag => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  onClick={() => {
                    if (sourceRef.current) {
                      sourceRef.current.focus();
                      setTimeout(() => {
                        sourceRef.current.insertTag(tag.value);
                      }, 10);
                    }
                  }}
                  size="small"
                  sx={{
                    backgroundColor: '#f0f0ff',
                    color: '#5757b0',
                    borderColor: '#5757b030',
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: '#e0e0ff',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                    },
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Leave empty to use the file that triggered the event.
          </Typography>
        </CardContent>
      </Card>

      {/* Where to save */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                WHERE
              </Box>
              to copy files
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 8px 20px rgba(87, 87, 176, 0.1)',
            },
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                flex: 1,
                padding: '12px 16px',
                minHeight: '28px',
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                overflow: 'hidden',
                borderBottom: { xs: '1px solid #eaeaf0', sm: 'none' },
              }}>
                <TagEditor
                    caseid={caseid}
                  ref={targetRef}
                  value={targetPath}
                  onChange={setTargetPath}
                  placeholder="Copy destination path (e.g. C:\\Folder\\{filename}{file_extension})"
                  minHeight="28px"
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'target')}
                  tags={tags}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleBrowseDirectory}
                disabled={browsingDisabled}
                sx={{
                  height: { xs: '40px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: { xs: '0', sm: '0 12px 0 0' },
                  backgroundColor: '#5757b0',
                  color: 'white',
                  m: 0,
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#4a4a90',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#c0c0d0',
                    color: 'white',
                  },
                  borderLeft: { sm: '1px solid #5757b060' },
                  transition: 'all 0.2s ease',
                }}
              >
                <BrowseIcon />
              </Button>
            </Box>

            <Box sx={{
              p: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              backgroundColor: '#fafbff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{
                fontWeight: 600,
                color: '#5757b0',
                fontSize: '0.85rem',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
              }}>
                Quick Tags:
              </Typography>
              {[
                { label: 'Filename', value: 'filename' },
                { label: 'Extension', value: 'file_extension' },
                { label: 'Folder Path', value: 'file_folder_path' },
                { label: 'Year', value: 'year' },
                { label: 'Month', value: 'month' },
                { label: 'Day', value: 'day' },
              ].map(tag => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  onClick={() => {
                    if (targetRef.current) {
                      targetRef.current.focus();
                      setTimeout(() => {
                        targetRef.current.insertTag(tag.value);
                      }, 10);
                    }
                  }}
                  size="small"
                  sx={{
                    backgroundColor: '#f0f0ff',
                    color: '#5757b0',
                    borderColor: '#5757b030',
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: '#e0e0ff',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                    },
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
          <FormControlLabel
            control={(
              <Checkbox
                checked={replaceIfExists}
                onChange={(e) => setReplaceIfExists(e.target.checked)}
              />
            )}
            label={t('workflow.copy_file_replace_if_exists_label', 'Replace target file if it already exists')}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('workflow.copy_file_replace_if_exists_help', 'When enabled, an existing file at the target path will be overwritten.')}
          </Typography>
        </CardContent>
      </Card>

      {/* Main Tag dropdown */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
        menuType={menuType}
        additionalTags={customDataTags}
      />

      {FolderBrowserComponent}
      {SourceFileBrowserComponent}

      <Dialog
        open={replaceWarningOpen}
        onClose={() => setReplaceWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('workflow.copy_file_replace_warning_title', 'Overwrite Existing Files')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t('workflow.copy_file_replace_warning_body', 'Replace-if-exists is enabled. Saving this action can overwrite existing files at the destination path.')}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            {t('workflow.copy_file_replace_warning_confirm', 'Do you want to save with this option enabled?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReplaceWarningOpen(false)}>
            {t('action_common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleConfirmReplaceSave}>
            {t('workflow.copy_file_replace_warning_save_anyway', 'Save Anyway')}
          </Button>
        </DialogActions>
      </Dialog>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCopyFile);
